/**
 * SAM-683 — English copy of the admin. The source of truth for the key list: es.ts must have
 * exactly these keys (the type enforces it).
 *
 * Keys are `area.thing` (dotted, camelCase segments): `common.*` for words shared across screens,
 * then one prefix per screen or feature (`business.*`, `team.*`, `payments.*`, `booking.*`, …).
 * `{name}` placeholders are filled by t(key, { name }).
 */
export const en = {
  // ── Shared ────────────────────────────────────────────────────────────────
  'common.loading': 'Loading…',
  'common.cancel': 'Cancel',
  'common.back': 'Back',
  'common.continue': 'Continue',
  'common.next': 'Next',
  'common.edit': 'Edit',
  'common.done': 'Done',
  'common.required': 'Required',
  'common.notAvailable': 'N/A',
  'common.status.active': 'Active',
  'common.status.inactive': 'Inactive',

  // ── Header / user menu ────────────────────────────────────────────────────
  'header.appName': 'Business Admin',
  'header.selectBusiness': 'Select Business',
  'header.user': 'User',
  'header.userInitial': 'U',
  'header.logOut': 'Log Out',

  // ── Business page (/business/:id) ─────────────────────────────────────────
  'business.tab.activity': 'Activity',
  'business.tab.services': 'Services',
  'business.tab.team': 'Team',
  'business.tab.settings': 'Settings',
  'business.section.profile': 'Profile',
  'business.section.schedule': 'Schedule',
  'business.section.closures': 'Closures & special days',
  'business.section.advanced': 'Advanced settings',
  'business.tabsLabel': 'Business sections',
  'business.sectionsLabel': 'Settings sections',
  'business.loading': 'Loading business…',
  'business.notFound': 'Business not found.',
  'business.backToList': 'Back to businesses',
  'business.leave.title': 'Leave without saving?',
  'business.leave.body': 'You have unsaved changes on this business. They will be lost.',
  'business.leave.bodyHours': 'Your changes to the opening hours will be lost.',
  'business.leave.stay': 'Keep editing',
  'business.leave.leave': 'Discard changes',
  'business.new': 'New business',
  'business.fallbackName': 'Business',

  // ── Team tab ──────────────────────────────────────────────────────────────
  'team.searchPlaceholder': 'Search team members…',
  'team.addMember': 'Add member',
  'team.loading': 'Loading team…',
  'team.error': 'Could not load the team.',
  'team.retry': 'Try again',
  'team.emptySearch': 'No team members match your search.',
  'team.empty': 'No team members for this business yet.',
  'team.frontDeskNote': 'Team members you add are reviewed by Security before they get access.',
  'team.col.member': 'Member',
  'team.col.role': 'Role',
  'team.col.status': 'Status',
  'team.chip.admin': 'admin',
  'team.chip.invitation': 'invitation',
  'team.chip.operator': 'operator',
  'team.chip.owner': 'owner',
  'team.status.invited': 'Invited',
  'team.role.admin': 'Admin',
  'team.role.frontDesk': 'Front desk',
  'team.noRole': 'No business role',
  'team.backToTeam': 'Back to team',
  'team.editDetails': 'Edit details',
  'team.keepActive': 'Keep active',
  'team.deactivating': 'Deactivating…',
  'team.confirmDeactivate': 'Confirm deactivate',
  'team.deactivate': 'Deactivate',
  'team.activating': 'Activating…',
  'team.activate': 'Activate',
  'team.identityDocument': 'Identity document',
  'team.idOnFile': 'On file',
  'team.idMissing': 'Missing',
  'team.idType.documentId': 'Document ID',
  'team.idType.passport': 'Passport',
  'team.idType.driverLicense': 'Driver License',
  'team.access': 'Access',
  'team.field.firstName': 'First name',
  'team.field.lastName': 'Last name',
  'team.field.dateOfBirth': 'Date of birth',
  'team.field.idType': 'ID type',
  'team.field.idNumber': 'ID number',
  'team.field.nationality': 'Nationality',
  'team.field.contactNumber': 'Contact number',
  'team.field.signIn': 'Sign-in',
  'team.field.role': 'Role',
  'team.field.business': 'Business',
  'team.field.status': 'Status',
  'team.field.added': 'Added',
  'team.field.lastChange': 'Last change',
  'team.identity': 'Identity',
  'team.document': 'Document',
  'team.contactAccess': 'Contact and access',
  'team.field.phone': 'Phone',
  'team.field.email': 'Email',
  'team.field.verified': 'Verified',

  // ── Reset password (team member) ──────────────────────────────────────────
  'reset.button': 'Reset password',
  'reset.title': 'Reset password?',
  'reset.send': 'Send reset code',
  'reset.sending': 'Sending…',
  'reset.sent': 'Reset code sent to {who}.',
  'reset.bodyEmail': 'We will email a verification code to',
  'reset.bodyBefore': 'Their current password stops working right away. To sign in again they choose',
  'reset.forgotPassword': 'Forgot password',
  'reset.bodyAfter': ', enter the code and set a new password.',
  'reset.reason.ownAccount': 'This is your own account. Use "Forgot password" on the sign-in page.',
  'reset.reason.noPermission': 'You need user management permission to reset passwords.',
  'reset.reason.otherBusiness': 'You can only reset passwords of your own business users.',
  'reset.reason.xhinola': 'Xhinola accounts cannot be reset from here.',
  'reset.reason.adminOnly': 'Only a tenant administrator can reset an administrator.',
  'reset.reason.inactive': 'The user is inactive. Activate them first.',
  'reset.reason.neverSignedIn': 'The user has not signed in yet, so there is no password to reset.',
  'reset.error.network': 'Could not reach the server. Check the connection and try again.',
  'reset.error.notDeployed': 'Password reset is not available on this environment yet.',
  'reset.error.forbidden': "You are not allowed to reset this user's password.",
  'reset.error.notFound': 'This user no longer exists.',
  'reset.error.noVerifiedContact': 'The user has no verified email or phone, so Cognito cannot send the reset code.',
  'reset.error.tooMany': 'Too many resets for this user. Try again later.',
  'reset.error.rejected': 'The reset request was not accepted.',
  'reset.error.generic': 'Could not reset the password. Try again.',

  // ── Payments (/payments) ──────────────────────────────────────────────────
  'payments.title': 'Transactions',
  'payments.filters': 'Filters',
  'payments.clear': 'Clear',
  'payments.searchPlaceholder': 'Search by reference, txn, card, amount...',
  'payments.col.reference': 'REFERENCE',
  'payments.col.txnRef': 'TXN REF',
  'payments.col.date': 'DATE',
  'payments.col.amount': 'AMOUNT',
  'payments.col.card': 'CARD',
  'payments.col.status': 'STATUS',
  'payments.col.refunded': 'REFUNDED',
  'payments.col.gateway': 'GATEWAY',
  'payments.col.business': 'BUSINESS',
  'payments.remaining': 'Remaining {amount}',
  'payments.status.success': 'Success',
  'payments.status.failed': 'Failed',
  'payments.status.voided': 'Voided',
  'payments.status.partially_refunded': 'Partially refunded',
  'payments.status.refunded': 'Refunded',
  'payments.filter.status': 'Status',
  'payments.filter.all': 'All',
  'payments.filter.dateRange': 'Date Range',
  'payments.filter.selectRange': 'Select range...',
  'payments.filter.minAmount': 'Min Amount',
  'payments.filter.maxAmount': 'Max Amount',

  // ── Client booking (/invitations/new) ─────────────────────────────────────
  'booking.businessIdRequired': 'Business ID required',
  'booking.service.pricesError': "Could not load this service's prices. Reload the page and try again.",
  'booking.service.pricesLoading': 'Loading service prices…',
  'booking.service.loadingShort': 'Loading services...',
  'booking.service.loading': 'Loading services…',
  'booking.service.loadError': 'Error loading services',
  'booking.service.noneBookable': 'No bookable services available',
  'booking.service.select': 'Select a service',
  'booking.service.noneSynced': 'No services for this business are synced to the booking engine yet.',
  'booking.access.event': 'Event',
  'booking.event.loadingShort': 'Loading events...',
  'booking.event.loading': 'Loading events…',
  'booking.event.loadError': 'Error loading events',
  'booking.event.none': 'No open events',
  'booking.event.select': 'Select an event',
  'booking.access.pickEvent': 'Please select an event first.',
  'booking.product.loading': 'Loading products...',
  'booking.product.loadError': 'Error loading products',
  'booking.product.none': 'No products available',
  'booking.product.select': 'Select a product',
  'booking.access.eyebrow': 'Step 1 of 4',
  'booking.access.title': 'Access',
  'booking.access.description': 'Who is this for, where are they going, and for how long.',
  'booking.access.destination': 'Destination',
  'booking.access.accessType': 'Access type',
  'booking.access.product': 'Product',
  'booking.access.service': 'Service',
  'booking.access.pickDestinationProducts': 'Please select a destination first to view available products.',
  'booking.access.pickDestinationServices': 'Please select a destination first to view available services.',
  'booking.access.howLong': 'How long?',
  'booking.access.pickDestination': 'Please select a destination first.',
  'booking.access.singleDayReason': 'Service bookings are for a single day — switch to "One day" above.',
  'booking.access.timeAndQuantities': 'Time & quantities',
  'booking.destination.loadingBusinesses': 'Loading businesses...',
  'booking.destination.errorBusinesses': 'Error loading businesses',
  'booking.destination.selectBusiness': 'Select a business',
  'booking.destination.loadingGroups': 'Loading estate groups...',
  'booking.destination.errorGroups': 'Error loading estate groups',
  'booking.destination.selectGroup': 'Select an estate group',
  'booking.destination.loadingBuildings': 'Loading buildings...',
  'booking.destination.allBuildings': 'All buildings (optional)',
  'booking.destination.pickGroupFirst': 'Please select an estate group first',
  'booking.destination.loadingEstates': 'Loading estates...',
  'booking.destination.errorEstates': 'Error loading estates',
  'booking.destination.selectEstate': 'Select an estate',
  'booking.destination.noEstates': 'No estates in this group',
  'booking.kind.residential': 'Residential',
  'booking.kind.business': 'Business',
  'booking.kind.event': 'Event',
  'booking.accessType.loadingBusiness': 'Loading business access types...',
  'booking.accessType.loadingResidential': 'Loading residential access types...',
  'booking.accessType.errorBusiness': 'Error loading business access types',
  'booking.accessType.errorResidential': 'Error loading residential access types',
  'booking.accessType.select': 'Select a pass type',
  'booking.readiness.oneDayNotReady': 'Pick a day and confirm Adults/Children in "How long?" above to see this service\'s availability.',
  'booking.readiness.multiDay': 'Service bookings are for a single day — switch to "One day" above to see this service\'s availability.',
  // readiness.* are only used in Spanish: English shows serviceDateReadiness's own sentence.
  'booking.readiness.maxDays': '(this service allows up to {n} day(s) per booking, and only one date can be booked at a time today.)',
  'booking.readiness.minDays': '(this service requires at least {n} day(s), and only one date can be booked at a time today.)',

  'booking.howLong.oneDay': 'One day',
  'booking.howLong.multipleDays': 'Multiple days',
  'booking.howLong.singleDayOnly': 'Service bookings are for a single day.',
  'booking.howLong.today': 'Today',
  'booking.howLong.selectRange': 'Select Date Range',
  'booking.howLong.rangePlaceholder': 'Click to select start and end dates',
  'booking.howLong.selectedRange': 'Selected Date Range',
  'booking.howLong.howMany': 'How many?',
  'booking.howLong.adults': 'Adults',
  'booking.howLong.adultsHint': 'Above 13 years old',
  'booking.howLong.children': 'Children',
  'booking.howLong.childrenHint': '2-12 years old',
  'booking.howLong.total': 'Total',
  'booking.howLong.guestsTotal': '{n} guests',
  'booking.phone.title': 'New pass',
  'booking.phone.close': 'Close',
  'booking.phone.stepOf': 'Step {n} of {total}',
  'booking.phone.whatDate': 'What date?',
  'booking.phone.tomorrow': 'Tomorrow',
  'booking.phone.moreDates': 'More dates',
  'booking.phone.calendar': 'Calendar',
  'booking.phone.pickDay': 'Choose a day',
  'booking.phone.from': 'From',
  'booking.phone.to': 'To',
  'booking.phone.howMany': 'How many guests?',
  'booking.phone.adultsHint': '13 years and older',
  'booking.phone.upTo': 'Up to {n} people.',
  'booking.phone.reading': 'Reading the ID…',
  'booking.phone.addIdPhoto': 'Add ID photo',
  'booking.phone.or': 'OR',
  'booking.phone.fromContacts': 'Add from contacts',
  'booking.phone.fromContactsHint': 'Guests who came before',
  'booking.phone.shareRegistration': 'Share registration link',
  'booking.phone.shareRegistrationHint': 'Guests fill in their own details',
  'booking.phone.scannedDocument': 'SCANNED DOCUMENT',
  'booking.phone.rescan': 'Rescan',
  'booking.phone.optional': 'Optional',
  'booking.phone.scanNote': 'Name and last name are required. The ID number is read from the photo if you leave it empty.',
  'booking.phone.firstName': 'First name',
  'booking.phone.lastName': 'Last name',
  'booking.phone.idNumber': 'ID number',
  'booking.phone.nationality': 'Nationality',
  'booking.phone.dob': 'Date of birth',
  'booking.phone.reachTitle': 'How do we reach them?',
  'booking.phone.reachHint': 'The main guest gets the pass by SMS.',
  'booking.phone.phoneNumber': 'Phone number',
  'booking.phone.brandNotAccepted': 'We do not accept {brand}. Use Visa, Mastercard or American Express.',
  'booking.phone.thatCard': 'that card',
  'booking.phone.payWithCard': 'Pay with card',
  'booking.phone.cardNote': 'The card is sent to SAM, which charges it. It is not stored on this device.',
  'booking.phone.mainGuest': 'Main guest',
  'booking.phone.companionN': 'Guest {n}',
  'booking.phone.bookingDetails': 'Booking details',
  'booking.phone.date': 'Date',
  'booking.phone.guests': 'Guests',
  'booking.phone.price': 'Price',
  'booking.phone.sentBySms': 'Pass sent by SMS to the main guest',
  'booking.phone.sharePass': 'Share pass',
  'booking.phone.done': 'Done',
  'booking.phone.createPass': 'Create pass',
  'booking.phone.passCreated': 'Pass created',
  'booking.phone.persons': 'Persons',
  'booking.phone.dates': 'Dates',
  'booking.phone.business': 'Business',
  'booking.phone.adultsN': '{n} adults',
  'booking.phone.adultN': '1 adult',
  'booking.phone.childN': '1 child',
  'booking.phone.childrenN': '{n} children',
  'booking.phone.allDay': 'All day',
  'booking.phone.enterManually': 'Enter details instead',
  'booking.phone.phoneIncomplete': 'Please enter a complete phone number',
  'booking.phone.registrationHint': 'We text the guest a link to register once the pass is created.',
  'booking.phone.fewer': 'Fewer {what}',
  'booking.phone.more': 'More {what}',
  'booking.howLong.ofMax': ' / {n} max',
  'booking.howLong.maxAdultsOne': 'This pass type allows a maximum of {n} adult.',
  'booking.howLong.maxAdultsMany': 'This pass type allows a maximum of {n} adults.',
  'booking.howLong.maxChildrenOne': 'This pass type allows a maximum of {n} child.',
  'booking.howLong.maxChildrenMany': 'This pass type allows a maximum of {n} children.',
  'booking.howLong.maxPerDay': 'This pass type allows a maximum of {n} guests per day.',

  'booking.slots.pickDate': 'Pick a date above ("How long?") to see this service\'s availability.',
  'booking.slots.loading': 'Loading availability…',
  'booking.slots.loadError': "Could not load this service's availability.",
  'booking.slots.localFallback': "Availability may be incomplete — this service isn't reading live from the booking engine.",
  'booking.slots.noneOnDate': 'This service has no availability on {date}. Pick a different date above.',
  'booking.slots.fullDay': 'Full-day booking on {date}{hours} — no time to pick.',
  'booking.slots.left': '{n} left',

  'booking.price.flatEvent': 'flat event price',
  'booking.price.perExtraGuest': '+{price} per extra guest',
  'booking.price.perUnit': '{price} / unit',
  'booking.rateGroup.label': 'Rate group',
  'booking.rateGroup.for': 'Rate group for {option}',
  'booking.rateGroup.choose': 'Choose…',
  'booking.unit.title': 'Cabana / unit',
  'booking.unit.whichOne': 'Which one is being booked?',
  'booking.unit.ariaLabel': 'Cabana or unit',
  'booking.unit.select': 'Select a cabana…',
  'booking.unit.describe': '{price} · {included} incl.',
  'booking.unit.max': 'max {max}',
  'booking.unit.priceNotSet': 'price not set',
  'booking.unit.fallbackName': 'Unit',
  'booking.unit.includesOne': '{price} includes {n} guest',
  'booking.unit.includesMany': '{price} includes {n} guests',
  'booking.unit.upTo': ', up to {max}',
  'booking.unit.extraOne': '{n} extra guest pays the per-guest rate',
  'booking.unit.extraMany': '{n} extra guests pay the per-guest rate',
  'booking.unit.allIncluded': 'all within the included guests',
  'booking.guests.one': '{n} guest',
  'booking.guests.many': '{n} guests',
  'booking.event.size': 'Event size',
  'booking.event.attendeesToSize': '{n} attendees → size',
  'booking.event.flatOnce': 'flat, charged once for the whole event.',
  'booking.event.noPrice': 'No price for this attendee count yet.',
  'booking.event.setCount': 'Set the attendee count above to see the size band.',
  'booking.season.pricesApply': '{season} prices apply',

  'booking.qty.cannotDerive': "This service's options can't be derived from Adults/Children automatically — set quantities manually.",
  'booking.qty.fromAdultsChildren': 'Guest count comes from Adults/Children above ({n} total).',
  'booking.qty.includedInUnit': '{n} included in the unit price',
  'booking.qty.includedFree': '{n} included free',
  'booking.qty.extra': '{n} extra',
  'booking.qty.billable': '{n} billable',
  'booking.qty.decrease': 'Decrease {item}',
  'booking.qty.increase': 'Increase {item}',
  'booking.qty.minimum': '{option}: minimum is {n}.',
  'booking.qty.maximum': '{option}: maximum is {n}.',
  'booking.qty.atLeastOne': 'Select at least one guest.',
  'booking.qty.noOptions': 'This service has no booking options configured.',
  'booking.qty.tierGroupManual': 'This service\'s guest tier_group ("{group}") has {n} tiers — set quantities manually.',
  'booking.addOns': 'Add-ons',
  'booking.addOnRequired': 'required',
  'booking.totalConfirmedByServer': 'The total price is confirmed by the server when the booking is submitted.',

  'booking.summary.title': 'Booking summary',
  'booking.summary.destination': 'Destination',
  'booking.summary.accessType': 'Access type',
  'booking.summary.product': 'Product',
  'booking.summary.service': 'Service',
  'booking.summary.date': 'Date',
  'booking.summary.time': 'Time',
  'booking.summary.fullDay': 'Full day',
  'booking.summary.guests': 'Guests',
  'booking.summary.total': '{n} total',
  'booking.summary.adultOne': '{n} adult',
  'booking.summary.adultMany': '{n} adults',
  'booking.summary.childOne': '{n} child',
  'booking.summary.childMany': '{n} children',
  'booking.summary.extraGuests': 'Extra guests',
  'booking.summary.event': 'Event',
  'booking.summary.perUnitPrices': 'Per-unit prices',
  'booking.summary.guestsIncluded': '{n} guests included',
  'booking.summary.flat': '{price} flat',
  'booking.summary.seasonPricing': 'Season pricing',
  'booking.summary.estimatedTotal': 'Estimated total',
  'booking.summary.nothingSelected': 'Nothing selected yet.',

  'booking.payment.title': 'Payment',
  'booking.payment.description': 'Card details are processed by CyberSource with 3DS — nothing here is stored unencrypted.',
  'booking.payment.when': '{date} at {time}',
  'booking.payment.cardOptional': 'Enter a card only if this booking is paid — the amount is confirmed by the server. Leave it blank for a free reservation.',

  // Card form labels (only passed to CreditCardInput in Spanish; English keeps its own messages)
  'card.number': 'Card Number',
  'card.holder': 'Card Holder Name',
  'card.expiry': 'Expiry Date',
  'card.cvv': 'CVV',
  'card.required': 'Required',
  'card.invalidNumber': 'Card number must be 16 digits',
  'card.invalidExpiry': 'Expiry date must be in MM/YY format',
  'card.expired': 'Card has expired',
  'card.invalidCvv': 'CVV must be 3 or 4 digits',
  'card.invalidName': 'Card holder name must contain only letters',

  // ── Invitation / booking confirmation ─────────────────────────────────────
  'invitation.action.shareLink': 'Share link',
  'invitation.action.print': 'Print',
  'invitation.action.confirm': 'Confirmed',
  'invitation.action.checkIn': 'Check In',
  'invitation.action.checkOut': 'Check Out',
  'invitation.action.cancel': 'Cancel',
  'invitation.status.pending': 'Pending',
  'invitation.status.confirmed': 'Confirmed',
  'invitation.status.verified': 'Verified',
  'invitation.status.checkedIn': 'Checked In',
  'invitation.status.checkedOut': 'Checked Out',
  'invitation.status.cancelled': 'Cancel',
  'invitation.status.expired': 'Expired',
  'invitation.status.processing': 'Processing',
  'invitation.status.unknown': 'Unknown',
  'invitation.guestType.guest': 'Guest',
  'invitation.guestType.business': 'Business',
  'invitation.guestType.vip': 'VIP',
  'invitation.guestType.event': 'Event',
  'invitation.guestType.other': 'Type {id}',
  'passLink.title': 'Payment link',
  'passLink.expires': 'expires at {time}',
  'passLink.copy': 'Copy payment link',
  'passLink.copyFailed': 'Could not copy the link',
  'passLink.smsFailed': 'The link was not sent by SMS. Copy it and send it to the guest.',
  'passConfirm.title': 'Confirm',
  'passConfirm.destination': 'Where the guest goes',
  'passConfirm.mainGuest': 'Main guest',
  'passConfirm.dates': 'Dates',
  'passConfirm.guests': 'Guests',
  'passConfirm.idDocument': 'ID document',
  'passConfirm.idScanned': 'Scanned',
  'passConfirm.idEntered': 'Entered',
  'passConfirm.idByLink': 'The guest completes it by link',
  'passConfirm.payment': 'Payment',
  'passConfirm.card': 'Card ····{last4}',
  'passConfirm.link': 'Payment link',
  'passConfirm.continue': 'Continue to send',
  'invitation.linkCopied': 'Link copied to clipboard!',
  'invitation.linkCopyFailed': 'Unable to copy link - access code not found',
  'invitation.noData': 'No pass data available',
  'invitation.moreActions': 'More actions',
  'invitation.scanQr': 'Scan QR Code',
  'invitation.field.type': 'Pass type',
  'invitation.field.guestType': 'Guest Type',
  'invitation.field.whereTo': 'Where to?',
  'invitation.field.when': 'When?',
  'invitation.field.guest': 'Guest',
  'invitation.field.mainGuest': 'Main Guest',
  'invitation.field.phone': 'Phone Number',
  'invitation.field.email': 'Email',
  'invitation.field.gate': 'Gate #',
  // SAM-708: invitations drawer
  'invitation.detail.hint.pending': 'Waiting for approval',
  'invitation.detail.hint.confirmed': 'Ready for arrival',
  'invitation.detail.hint.verified': 'Verified, ready for arrival',
  'invitation.detail.hint.checkedIn': 'Guest is on site',
  'invitation.detail.hint.checkedOut': 'Guest has left',
  'invitation.detail.hint.cancelled': 'This pass was cancelled',
  'invitation.detail.awaitingPayment': 'Waiting for payment',
  'invitation.detail.awaitingPaymentExpires': 'Waiting for payment · expires {when}',
  'invitation.detail.hint.expired': 'This pass expired',
  'invitation.detail.do.confirm': 'Confirm',
  'invitation.detail.do.checkIn': 'Check in',
  'invitation.detail.do.checkOut': 'Check out',
  'invitation.detail.cancelInvitation': 'Cancel pass',
  'invitation.detail.cancelShort': 'Cancel',
  'invitation.detail.residential': 'Residential',
  'invitation.detail.business': 'Business',
  'invitation.detail.guestFallback': 'Guest',
  'invitation.detail.visit': 'Visit',
  'invitation.detail.whereTo': 'Where to',
  'invitation.detail.when': 'When',
  'invitation.detail.party': 'Party',
  'invitation.detail.gates': 'Gates',
  'invitation.detail.allGates': 'All gates',
  'invitation.detail.oneDay': 'One day',
  'invitation.detail.days': '{count} days',
  'invitation.detail.noDate': 'Date not specified',
  'invitation.detail.adults': '{count} adults',
  'invitation.detail.adult': '1 adult',
  'invitation.detail.children': '{count} children',
  'invitation.detail.child': '1 child',
  'invitation.detail.pass': 'Access pass',
  'invitation.detail.passHint': 'The guest shows this QR at the gate.',
  'invitation.detail.share': 'Share',
  'invitation.detail.sms': 'SMS',
  'invitation.detail.copyLink': 'Copy link',
  'invitation.detail.printPass': 'Print pass',
  'invitation.detail.linkCopied': 'Link copied',
  'invitation.detail.noLink': 'This pass has no link yet',
  'invitation.detail.whatsappText': 'Hi {name}, here is your access pass: {link}',
  'invitation.detail.guests': 'Guests ({count})',
  'invitation.detail.mainGuest': 'Main guest',
  'invitation.detail.companion': 'Companion {n}',
  'invitation.detail.idOnFile': 'ID on file',
  'invitation.detail.noId': 'No ID',
  'invitation.detail.moreDetails': 'More details',
  'invitation.detail.created': 'Created',
  'invitation.detail.channel': 'Channel',
  'invitation.detail.passCode': 'Pass code',
  'invitation.detail.payment': 'Payment',
  'invitation.detail.payment.paid': 'Paid',
  'invitation.detail.payment.voided': 'Voided',
  'invitation.detail.payment.refunded': 'Refunded',
  'invitation.detail.payment.partiallyRefunded': 'Partially refunded',
  'invitation.detail.payment.failed': 'Failed',
  'invitation.detail.payment.pending': 'Pending',
  'invitation.gateAll': 'all',
  'invitation.date.notSpecified': 'Date not specified',
  'invitation.date.invalid': 'Invalid date',
  'invitation.date.notAvailable': 'Date not available',
  'userEstate.title': 'Estates',
  'userEstate.summary': '{active} active · {pending} pending · {declined} declined',
  'userEstate.add': 'Add estate',
  'userEstate.empty': 'This user has no estates yet.',
  'userEstate.loadError': 'Could not load the estates.',
  'userEstate.section.pending': 'Pending approval',
  'userEstate.section.active': 'Active',
  'userEstate.section.declined': 'Declined',
  'userEstate.col.unit': 'Unit',
  'userEstate.col.role': 'Role',
  'userEstate.col.requested': 'Requested',
  'userEstate.col.expires': 'Expires',
  'userEstate.col.actions': 'Actions',
  'userEstate.adminOnly': 'Admin only',
  'userEstate.noGroup': 'No group',
  'userEstate.noRole': 'No role',
  'userEstate.noExpiration': 'No expiration',
  'userEstate.expiresIn': 'in {n} days',
  'userEstate.expiresIn1': 'in 1 day',
  'userEstate.expiresToday': 'today',
  'userEstate.expired': 'expired',
  'userEstate.approve': 'Approve',
  'userEstate.decline': 'Decline',
  'userEstate.revoke': 'Revoke',
  'userEstate.reactivate': 'Reactivate',
  'userEstate.revokeTitle': 'Revoke access?',
  'userEstate.revokeBody': '{unit} will stop being active for this user. You can reactivate it later.',
  'userEstate.back': 'Back',
  'userEstate.roleLabel': 'Role of {unit}',
  'userEstate.savingRole': 'Saving…',
  'userEstate.expiryLabel': 'Expiration of {unit}',
  'userEstate.prevMonth': 'Previous month',
  'userEstate.nextMonth': 'Next month',
  'userEstate.expiryUpdated': 'Expiration updated',
  'userEstate.expiryFailed': 'Could not update the expiration',
  'userEstate.statusUnavailable': 'This status does not exist for the tenant.',
  'invitation.confirm.back': 'Back',
  'invitation.confirm.confirmTitle': 'Confirm pass',
  'invitation.confirm.confirmButton': 'Confirm',
  'invitation.confirm.confirmBody': 'Are you sure you want to confirm this pass?',
  'invitation.confirm.checkInBody': 'Are you sure you want to check in this guest?',
  'invitation.confirm.checkOutBody': 'Are you sure you want to check out this guest?',
  'invitation.confirm.cancelBody': 'Are you sure you want to cancel this pass? If it has a payment, the payment will be voided. This action cannot be undone.',
  'invitation.confirm.guest': 'Guest:',
  'invitation.confirm.destination': 'Destination:',
  'user.businessRequired': "Assign a business to this user before adding a business role.",
  'user.businessRequiredHint': "Set it in the Business section below, save, then add the role.",
  'passError.pass_expired': "This pass has already ended, so it cannot be changed or cancelled.",
  'passError.invalid_dates': "The dates are not valid: a pass cannot end in the past or before it starts.",
  'passError.guest_idnum_locked': "The ID number of the main guest cannot be changed. For another person, create another pass.",
  'passError.use_invitation_endpoint': "This change has to be made on the pass itself, not from this screen. Reload the page and try again.",
  'passPayment.void_window_closed': "The bank's cutoff time to void this payment has passed, so the pass was not cancelled and the payment was not changed.",
  'passPayment.CANNOT_VOID_CHECKED_IN': "This pass is already checked in, so its payment cannot be voided and the pass cannot be cancelled.",
  'passPayment.pass_has_payment': "This pass has a payment that has to be voided first. Nothing was cancelled; reload and try again.",
  'passPayment.payment_in_progress': "The payment of this pass is still being processed. Try again in a few minutes.",
  'passPayment.PAYMENT_LEDGER_UNAVAILABLE': "The payment of this pass cannot be checked right now, so nothing was cancelled. Try again.",
  'passPayment.forbidden': "The server refused to cancel this pass. If it has a payment, cancelling voids it, which needs permission to void payments.",
  'appUser.deleted': "Deleted account: no Cognito account carries this user's id ({lookup}). Nothing to manage here.",
  'appUser.otherRow': "The account with this e-mail belongs to user {id}, not to this record.",
  'appUser.openUser': "Open user {id}",
  'appUser.unverified': "This Cognito account cannot be confirmed as belonging to this record (found by e-mail, no user id returned). Read-only: no account actions.",

  // ── Phone: menu, Passes and Activity at 375 px (SAM-944) ─────────────────
  'header.openMenu': 'Open menu',
  'menu.logOut': 'Log out',
  'menu.roleAdmin': 'Admin',
  'menu.roleXhinola': 'Xhinola',
  'menu.roleFrontDesk': 'Front desk',
  'menu.roleEstateGroupAdmin': 'Estate group admin',
  'menu.roleEstateGroupFrontDesk': 'Estate group front desk',
  'menu.roleBusinessAdmin': 'Owner / Admin',
  'menu.roleBusinessOperator': 'Operator',
  'phone.newPass': 'New pass',
  'phonePasses.title': 'Passes',
  'phonePasses.search': 'Search by guest, booking code or PIN',
  'phonePasses.filterLabel': 'Show passes',
  'phonePasses.filter.upcoming': 'Upcoming',
  'phonePasses.filter.today': 'Today',
  'phonePasses.filter.past': 'Past',
  'phonePasses.filter.statusGroup': 'Status',
  'phonePasses.empty': 'No passes to show.',
  'phonePasses.allDay': 'All day',
  'phonePasses.todayWhen': 'Today · {when}',
  'phonePasses.business': 'Business',
  'phonePasses.guest': 'Guest',
  'phonePasses.bookingCode': 'Booking {code}',
  'phonePasses.byHost': 'by {host}',
  'activityPhone.title': 'Activity',
  'activityPhone.viewLabel': 'Show',
  'activityPhone.view.bookings': 'Bookings',
  'activityPhone.view.passes': 'Passes',
  'activityPhone.view.transactions': 'Transactions',
  'activityPhone.search.bookings': 'Search guest or service',
  'activityPhone.search.passes': 'Search guest or destination',
  'activityPhone.search.transactions': 'Search payer, card or reference',
  'activityPhone.filtersLabel': 'Filters',
  'activityPhone.chip.today': 'Today',
  'activityPhone.chip.week': 'This week',
  'activityPhone.chip.month': 'This month',
  'activityPhone.chip.custom': 'Custom dates',
  'activityPhone.chip.checkedIn': 'Checked in',
  'activityPhone.chip.confirmed': 'Confirmed',
  'activityPhone.chip.cancelled': 'Cancelled',
  'activityPhone.chip.paid': 'Paid',
  'activityPhone.chip.refunded': 'Refunded',
  'activityPhone.from': 'From',
  'activityPhone.to': 'To',
  'activityPhone.pickDay': 'Choose a day',
  'activityPhone.empty.bookings': 'No bookings for this filter.',
  'activityPhone.empty.passes': 'No passes for this filter.',
  'activityPhone.empty.transactions': 'No transactions for this filter.',
  'activityPhone.failed': 'Couldn’t load all of the activity.',
  'activityPhone.retry': 'Try again',
  'activityPhone.guestsOne': '{n} guest',
  'activityPhone.guestsMany': '{n} guests',
  'activityPhone.createdBy': 'Created by {name}',

  // ── Phone: Admin Users, Businesses, Contacts and Events lists at 375 px (SAM-1308) ──
  'phoneLists.adminUsers.search': 'Search admin users',
  'phoneLists.adminUsers.summary': '{n} admin users',
  'phoneLists.adminUsers.create': 'Create business user',
  'phoneLists.adminUsers.otherRole': 'Other role…',
  'phoneLists.adminUsers.empty': 'No admin users to show.',
  'phoneLists.adminUsers.disabled': 'Disabled',
  'phoneLists.adminUsers.status.CONFIRMED': 'Confirmed',
  'phoneLists.adminUsers.status.FORCE_CHANGE_PASSWORD': 'Password pending',
  'phoneLists.adminUsers.status.UNCONFIRMED': 'Unconfirmed',
  'phoneLists.business.title': 'Businesses',
  'phoneLists.business.search': 'Search businesses',
  'phoneLists.business.summary': '{n} businesses',
  'phoneLists.business.filterLabel': 'Filter by status',
  'phoneLists.business.all': 'All',
  'phoneLists.business.active': 'Active',
  'phoneLists.business.inactive': 'Inactive',
  'phoneLists.business.empty': 'No businesses found.',
  'phoneLists.business.loading': 'Loading businesses…',
  'phoneLists.business.open': 'Open · {label}',
  'phoneLists.business.closed': 'Closed · {label}',
  'phoneLists.business.unnamed': 'Unnamed',
  'phoneLists.contacts.title': 'Contacts',
  'phoneLists.contacts.search': 'Search contacts',
  'phoneLists.contacts.empty': 'No contacts available.',
  'phoneLists.contacts.loading': 'Loading contacts…',
  'phoneLists.contacts.untitled': 'Contact',
  'phoneLists.contacts.guest': 'Guest {id}',
  'phoneLists.contacts.from': 'Invitation from {date}',
  'phoneLists.events.title': 'Events',
  'phoneLists.events.search': 'Search events',
  'phoneLists.events.filterLabel': 'Show events',
  'phoneLists.events.all': 'All',
  'phoneLists.events.empty': 'No matching events.',
  'phoneLists.events.none': 'No events yet.',
  'phoneLists.events.people': '{n} people',
  'phoneLists.events.waiting': 'Waiting for {tenant}',
  'phoneLists.events.waitingApproval': 'Waiting for approval',
  'phoneLists.events.untitled': 'Untitled event',
};

export type MessageKey = keyof typeof en;
